import { FC, useMemo } from 'react'
import Select from 'components/base/forms/Select'
import { useGetGroupsQuery } from 'common/services/useGroup'
import { usePermission, useUser } from 'common/services/useUser'
import { Permission } from 'common/types/enums'

type Option = { value: number; label: string }

type GroupSelectType = {
  className?: string
  defaultValue?: number
  disabled?: boolean
  excludeGroups?: number[]
  label?: string
  onChange: (v: Option) => void
  value: number | number[]
}

const GroupSelect: FC<GroupSelectType> = ({
  className,
  defaultValue,
  disabled,
  excludeGroups,
  label,
  onChange,
  value,
}) => {
  const isGroupOnly = usePermission(Permission.GROUP_ONLY)
  const { data: user } = useUser({})
  const { data: _data } = useGetGroupsQuery({}, { skip: isGroupOnly })

  const data = useMemo(() => {
    return isGroupOnly ? user?.groups || [] : _data?.results || []
  }, [isGroupOnly, user?.groups, _data?.results])

  const options = useMemo(
    () => data.map((v) => ({ label: v.name, value: v.id })),
    [data],
  )

  const selected = options.filter((o) =>
    Array.isArray(value) ? value.includes(o.value) : o.value === value,
  )

  const filtered = useMemo(() => {
    let result = defaultValue
      ? options.filter((o) => o.value !== defaultValue)
      : options
    if (excludeGroups) {
      result = result.filter((o) => !excludeGroups.includes(o.value))
    }
    return result
  }, [options, defaultValue, excludeGroups])

  return (
    <div className={className}>
      {label && <label className='fw-semibold fs-lg mb-2'>{label}</label>}
      <div className='d-flex gap-4'>
        {data.length === 0 ? (
          <div className='text-center'>
            You have no groups, you can set these up within Team settings.
          </div>
        ) : (
          <Select
            isDisabled={disabled}
            className='w-100'
            value={selected}
            onChange={(v: any) => onChange(v)}
            options={filtered}
          />
        )}
      </div>
    </div>
  )
}

export default GroupSelect
